import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { User } from '@supabase/supabase-js';

type UserRole = 'admin' | 'formateur' | 'etudiant' | null;

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  // Use refs to track initialization state and prevent loops
  const isInitialized = useRef(false);
  const authCheckComplete = useRef(false);
  const roleCheckAttempted = useRef(false);
  const isMounted = useRef(true);
  const retryCount = useRef(0);
  const MAX_RETRIES = 3;
  
  // Store deployment-specific debug info
  useEffect(() => {
    if (import.meta.env.PROD) {
      try {
        localStorage.setItem('lastAuthCheck', new Date().toISOString());
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, []);
  
  // Create a stable fetchUserRole function with useCallback
  const fetchUserRole = useCallback(async (userId: string, retry = false) => {
    if (!userId || (roleCheckAttempted.current && !retry)) return null;
    
    roleCheckAttempted.current = true;
    
    try {
      console.log('Fetching role for user:', userId);
      
      // Try to get from sessionStorage first (production only fallback)
      if (import.meta.env.PROD) {
        try {
          const storedRole = sessionStorage.getItem('userRole');
          if (storedRole) {
            console.log('Using cached role from session storage:', storedRole);
            // Still proceed with the fetch, but we have a fallback
            if (isMounted.current) {
              setUserRole(storedRole as UserRole);
            }
          }
        } catch (e) {
          // Ignore storage errors
        }
      }
      
      const { data, error } = await supabase
        .from('user_roles')
        .select('role, nom, prenom')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        
        // Add retry mechanism for transient errors
        if (retryCount.current < MAX_RETRIES) {
          console.log(`Retrying role fetch (attempt ${retryCount.current + 1}/${MAX_RETRIES})...`);
          retryCount.current++;
          
          // Exponential backoff for retries
          const backoffTime = Math.pow(2, retryCount.current) * 500;
          setTimeout(() => {
            if (isMounted.current) {
              fetchUserRole(userId, true);
            }
          }, backoffTime);
        } else {
          // After max retries, use session storage as fallback (if available)
          try {
            const storedRole = sessionStorage.getItem('userRole');
            if (storedRole && isMounted.current) {
              console.log('Using stored role as fallback after max retries:', storedRole);
              setUserRole(storedRole as UserRole);
            }
          } catch (e) {
            // Ignore storage errors
          }
        }
        
        return null;
      }
      
      console.log('Role data received:', data);
      
      if (data && data.role) {
        if (isMounted.current) {
          setUserRole(data.role as UserRole);
          
          // For diagnostic purposes in production, save role to sessionStorage
          try {
            sessionStorage.setItem('userRole', data.role);
            if (data.nom) sessionStorage.setItem('userName', `${data.prenom || ''} ${data.nom}`);
            localStorage.setItem('lastRoleCheck', new Date().toISOString());
          } catch (e) {
            // Ignore storage errors
          }
        }
        return data.role as UserRole;
      } else {
        console.log('No role found for user');
        if (isMounted.current) {
          setUserRole(null);
          
          // Clear role from sessionStorage
          try {
            sessionStorage.removeItem('userRole');
            sessionStorage.removeItem('userName');
          } catch (e) {
            // Ignore storage errors
          }
        }
        return null;
      }
    } catch (error) {
      console.error('Error in fetchUserRole:', error);
      if (isMounted.current) {
        setUserRole(null);
      }
      return null;
    }
  }, []);

  // Cleanup function to ensure we don't update unmounted component
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Single effect for auth initialization to prevent cascading effects
  useEffect(() => {
    // Skip if already initialized to prevent loops
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    console.log('Initializing auth state...');
    
    // For production diagnostics
    if (import.meta.env.PROD) {
      try {
        localStorage.setItem('authInitStart', new Date().toISOString());
      } catch (e) {
        // Ignore
      }
    }
    
    // Safety timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (isLoading && isMounted.current) {
        console.warn('Auth loading timeout reached, forcing completion');
        
        // For production, try to recover using stored data
        if (import.meta.env.PROD) {
          try {
            const storedRole = sessionStorage.getItem('userRole');
            const storedSession = localStorage.getItem('sb-supabase-auth-token');
            
            if (storedSession) {
              const sessionData = JSON.parse(storedSession);
              if (sessionData) {
                console.log('Recovered session from localStorage');
                if (storedRole) {
                  console.log('Recovered role from sessionStorage:', storedRole);
                  setUserRole(storedRole as UserRole);
                }
              }
            }
          } catch (e) {
            console.error('Error recovering from storage:', e);
          }
        }
        
        setIsLoading(false);
      }
    }, 8000); // Increased timeout for better reliability in production
    
    // Main auth initialization function
    const initializeAuth = async () => {
      try {
        // Get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          if (isMounted.current) {
            setUser(null);
            setUserRole(null);
            setIsLoading(false);
          }
          return;
        }
        
        if (session?.user) {
          console.log('Session found, user is logged in:', session.user.email);
          if (isMounted.current) {
            setUser(session.user);
            await fetchUserRole(session.user.id);
          }
        } else {
          console.log('No session found, user is not logged in');
          if (isMounted.current) {
            setUser(null);
            setUserRole(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        // Always mark auth check as complete and end loading
        authCheckComplete.current = true;
        if (isMounted.current) {
          setIsLoading(false);
        }
        
        // For production diagnostics
        if (import.meta.env.PROD) {
          try {
            localStorage.setItem('authInitComplete', new Date().toISOString());
          } catch (e) {
            // Ignore
          }
        }
      }
    };

    // Set up auth state change listener - only once!
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event);
        
        // Skip processing if component unmounted
        if (!isMounted.current) return;
        
        // For production diagnostics
        if (import.meta.env.PROD) {
          try {
            localStorage.setItem('lastAuthEvent', event);
            localStorage.setItem('lastAuthEventTime', new Date().toISOString());
          } catch (e) {
            // Ignore
          }
        }
        
        // Reset role check flag on sign in or sign out
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          roleCheckAttempted.current = false;
          retryCount.current = 0;
        }
        
        if (session?.user) {
          setUser(session.user);
          // Only fetch role if we haven't already tried or on specific events
          if (!roleCheckAttempted.current || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            await fetchUserRole(session.user.id);
          }
        } else {
          setUser(null);
          setUserRole(null);
          
          // Clear any saved role data
          try {
            sessionStorage.removeItem('userRole');
            sessionStorage.removeItem('userName');
          } catch (e) {
            // Ignore storage errors
          }
        }
        
        // Always ensure loading completes after auth state change
        setIsLoading(false);
      }
    );

    // Initialize auth state
    initializeAuth();

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchUserRole]); // Only stable dependencies

  const login = async (email: string, password: string) => {
    try {
      if (isMounted.current) {
        setIsLoading(true);
      }
      
      roleCheckAttempted.current = false;
      retryCount.current = 0;
      
      // For production diagnostics
      if (import.meta.env.PROD) {
        try {
          sessionStorage.setItem('loginAttemptTime', new Date().toISOString());
        } catch (e) {
          // Ignore
        }
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      // Store debug info in sessionStorage for troubleshooting
      try {
        sessionStorage.setItem('lastLogin', new Date().toISOString());
        sessionStorage.setItem('loginSuccess', 'true');
        sessionStorage.setItem('loginEmail', email); // Store for diagnostics
      } catch (e) {
        // Ignore storage errors
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error during login:', error);
      
      // Store debug info
      try {
        sessionStorage.setItem('loginError', (error as Error).message);
        sessionStorage.setItem('loginFailTime', new Date().toISOString());
      } catch (e) {
        // Ignore storage errors
      }
      
      return { 
        success: false, 
        error: (error as Error).message 
      };
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // L'inscription publique est désactivée par design : les comptes sont
  // créés exclusivement par un admin via la page /admin/users qui appelle
  // l'edge function `admin-create-user` (voir AdminUsers.tsx).
  // Cette fonction est conservée uniquement pour compat signature ; elle
  // renvoie toujours un échec explicite.
  const register = async (_email: string, _password: string, _role?: UserRole, _nom?: string, _prenom?: string) => {
    return {
      success: false,
      error:
        "L'inscription libre est désactivée. Veuillez contacter un administrateur pour créer un compte.",
    };
  };

  const logout = async () => {
    try {
      if (isMounted.current) {
        setIsLoading(true);
      }
      
      // First clear our state
      if (isMounted.current) {
        setUserRole(null);
      }
      
      // Then sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear any stored session data
      try {
        sessionStorage.removeItem('userRole');
        sessionStorage.removeItem('userName');
        sessionStorage.removeItem('lastPath');
        localStorage.removeItem('sb-supabase-auth-token');
      } catch (e) {
        // Ignore storage errors
      }
      
      // Navigate after signout is complete
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      if (isMounted.current) {
        setUser(null);
        setIsLoading(false);
      }
    }
  };

  // For production diagnostics
  useEffect(() => {
    if (import.meta.env.PROD) {
      console.log(`Auth state [${new Date().toISOString()}]:`, { 
        userExists: !!user, 
        userEmail: user?.email, 
        userRole, 
        isLoading 
      });
      
      // Store current auth state for debugging (handle errors)
      try {
        sessionStorage.setItem('lastAuthState', JSON.stringify({
          hasUser: !!user,
          role: userRole,
          isLoading,
          timestamp: new Date().toISOString()
        }));
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [user, userRole, isLoading]);

  return {
    user,
    userRole,
    isLoading,
    login,
    register,
    logout,
  };
}